import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

const TEAL = '#00a99d';
const NAVY = '#1a1a2e';

const TermsOfService = () => (
  <div style={{ minHeight: '100vh', background: '#fff', fontFamily: "'Inter', system-ui, sans-serif", color: NAVY }}>
    {/* Nav */}
    <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', boxShadow: '0 1px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Building2 style={{ width: 28, height: 28, color: TEAL }} />
          <span style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
        </Link>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#595959', textDecoration: 'none' }}>
          <ArrowLeft style={{ width: 16, height: 16 }} /> Back to Home
        </Link>
      </div>
    </nav>

    <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>
      <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 8 }}>Terms of Service</h1>
      <p style={{ color: '#595959', marginBottom: 32 }}>Last updated: March 27, 2026</p>

      <div style={{ lineHeight: 1.8, color: '#333', fontSize: 15 }}>
        <p>Welcome to Realty JAG. These Terms of Service ("Terms") govern your use of the Realty JAG platform operated by Realty JAG LLC ("Company", "we", "us", or "our"). By accessing or using our Service, you agree to be bound by these Terms.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>1. Account Registration</h2>
        <p>To use the Service, you must create an account and provide accurate, complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
        <p>You must be at least 18 years old to use this Service. By creating an account, you represent that you have the legal authority to enter into these Terms.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>2. Subscription Plans & Free Trial</h2>
        <p>Realty JAG offers subscription-based plans (Basic, Pro, Expert, Enterprise). All plans include a <strong>7-day free trial</strong> period. A valid credit card is required to start the free trial.</p>
        <p>After the trial period, your selected plan will automatically begin billing at the published monthly rate. You may cancel at any time before the trial ends to avoid being charged.</p>
        <p>Subscription fees are billed monthly through Stripe. All fees are non-refundable except as expressly provided in these Terms or required by law.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>3. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
          <li>Upload false, misleading, or fraudulent information</li>
          <li>Attempt to gain unauthorized access to other accounts or our systems</li>
          <li>Use the Service to harass, abuse, or harm other users</li>
          <li>Interfere with or disrupt the Service or its infrastructure</li>
          <li>Scrape, data mine, or extract data from the Service without authorization</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>4. Landlord Responsibilities</h2>
        <p>Landlords using the platform are responsible for:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li>Ensuring all property information is accurate and up-to-date</li>
          <li>Complying with all applicable landlord-tenant laws in their jurisdiction</li>
          <li>Properly managing tenant relationships and lease agreements</li>
          <li>Setting up and maintaining their Stripe Connect account for receiving payments</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>5. Tenant Responsibilities</h2>
        <p>Tenants using the platform are responsible for:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li>Making timely rent payments as specified in their lease</li>
          <li>Reporting maintenance issues promptly and accurately</li>
          <li>Maintaining the accuracy of their account information</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>6. Payments & Stripe</h2>
        <p>All payment processing is handled by Stripe, Inc. By using our payment features, you also agree to Stripe's <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" style={{ color: TEAL }}>Terms of Service</a>. We do not store credit card information on our servers.</p>
        <p>Late fees may be applied automatically as configured by the landlord and agreed upon in the lease terms.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>7. Intellectual Property</h2>
        <p>The Service and its original content, features, and functionality are owned by Realty JAG LLC and are protected by copyright, trademark, and other intellectual property laws.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>8. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law, Realty JAG LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of the Service.</p>
        <p>Our total liability for any claim arising from or relating to these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>9. Disclaimer</h2>
        <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied. Realty JAG LLC does not provide legal, tax, or financial advice. Users should consult with qualified professionals for such matters.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>10. Termination</h2>
        <p>We may terminate or suspend your account at any time for violations of these Terms. You may cancel your subscription at any time through the billing portal or by contacting us. Upon termination, your right to use the Service will cease immediately.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>11. Governing Law</h2>
        <p>These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts located in Palm Beach County, Florida.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>12. Changes to Terms</h2>
        <p>We reserve the right to modify these Terms at any time. We will notify users of material changes via email or a prominent notice on the Service. Continued use of the Service after changes constitutes acceptance of the modified Terms.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>13. Contact Us</h2>
        <p>If you have questions about these Terms, please contact us:</p>
        <div style={{ background: '#f7f8fa', padding: 24, borderRadius: 12, marginTop: 12, border: '1px solid #e5e7eb' }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Realty JAG LLC</p>
          <p style={{ margin: '0 0 4px' }}>350 N Federal Hwy, 715S</p>
          <p style={{ margin: '0 0 4px' }}>Boynton Beach, FL 33435</p>
          <p style={{ margin: '0 0 4px' }}>Email: <a href="mailto:contact@realtyjag.com" style={{ color: TEAL }}>contact@realtyjag.com</a></p>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer style={{ background: NAVY, padding: '24px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
          &copy; {new Date().getFullYear()} Realty JAG LLC. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
);

export default TermsOfService;
